import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBase } from '../lib/api';

const AuthContext = createContext(null);

const API = getApiBase();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    // Sin token no hay sesión: no llamar a /auth/me (evita 401 y mensajes en consola en /admin/login).
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (loginField, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { login: loginField, password },
      { withCredentials: true }
    );
    const data = response.data || {};
    if (data.mfa_required || data.totp_setup_required) {
      return data;
    }
    const { token: newToken, ...userData } = data;
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    }
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, getAuthHeaders, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
